import React, { useState, useEffect } from 'react';

/**
 * Navigation Component
 * Tab-based navigation for switching between different views
 * Includes hamburger menu for mobile devices
 *
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab ('profile' | 'generate' | 'history' | 'export')
 * @param {Function} props.onTabChange - Callback function when tab is clicked
 */
export default function Navigation({ activeTab, onTabChange }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 640);
            if (window.innerWidth >= 640) setIsMenuOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTabClick = (tab) => {
        onTabChange(tab);
        setIsMenuOpen(false);
    };

    return (
        <nav className="main-navigation">
            {isMobile && (
                <button
                    className="hamburger-button"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label={isMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
                    aria-expanded={isMenuOpen}
                >
                    {isMenuOpen ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="3" y1="12" x2="21" y2="12"></line>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                    )}
                </button>
            )}
            {isMenuOpen && isMobile && (
                <div className="menu-overlay" onClick={() => setIsMenuOpen(false)} />
            )}
            <div className={`nav-tabs ${isMobile ? 'mobile' : ''} ${isMenuOpen ? 'open' : ''}`}>
                <button
                    className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => handleTabClick('profile')}
                    aria-current={activeTab === 'profile' ? 'page' : undefined}
                >
                    <svg
                        className="nav-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>ملفي الشخصي</span>
                </button>

                <button
                    className={`nav-tab ${activeTab === 'generate' ? 'active' : ''}`}
                    onClick={() => handleTabClick('generate')}
                    aria-current={activeTab === 'generate' ? 'page' : undefined}
                >
                    <svg
                        className="nav-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <span>إنشاء سيرة ذاتية</span>
                </button>

                <button
                    className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => handleTabClick('history')}
                    aria-current={activeTab === 'history' ? 'page' : undefined}
                >
                    <svg
                        className="nav-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M3 3v5h5" />
                        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                        <path d="M12 7v5l4 2" />
                    </svg>
                    <span>السجل</span>
                </button>

                <button
                    className={`nav-tab ${activeTab === 'export' ? 'active' : ''}`}
                    onClick={() => handleTabClick('export')}
                    aria-current={activeTab === 'export' ? 'page' : undefined}
                >
                    <svg
                        className="nav-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span>تصدير/استيراد</span>
                </button>
            </div>
        </nav>
    );
}
